/**
 * AI Service API Functions
 * Based on ai-swagger.json OpenAPI specification
 */

import { AIServiceAPI } from "@/lib/axios";
import type {
  ITaskSubmission,
  ITaskStatus,
  ISegmentReplaceRequest,
  ISashOverlayRequest,
  ISashOverlayMultiFrameRequest,
  IDeepBottomRailRequest,
} from "../types";

/**
 * Submit a segment replace task
 * Endpoint: POST /api/segment-replace
 */
export const segmentReplaceApi = async (
  data: ISegmentReplaceRequest,
  signal?: AbortSignal
): Promise<ITaskSubmission> => {
  const formData = new FormData();

  formData.append("house_image", data.house_image);
  formData.append("template_image", data.template_image);

  if (data.points) {
    formData.append("points", data.points);
  }

  if (data.boxes) {
    formData.append("boxes", data.boxes);
  }

  if (data.online_blending !== undefined) {
    formData.append("online_blending", String(data.online_blending));
  }

  if (data.color) {
    formData.append("color", data.color);
  }

  const response = await AIServiceAPI.post<ITaskSubmission>(
    "/api/segment-replace",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      signal,
    }
  );

  return response.data;
};

/**
 * Submit a sash overlay task
 * Endpoint: POST /api/sash-overlay
 */
export const sashOverlayApi = async (
  data: ISashOverlayRequest
): Promise<ITaskSubmission> => {
  const formData = new FormData();

  formData.append("window_image", data.window_image);

  if (data.top_grid) {
    formData.append("top_grid", data.top_grid);
  }

  if (data.bottom_grid) {
    formData.append("bottom_grid", data.bottom_grid);
  }

  if (data.opacity !== undefined) {
    formData.append("opacity", String(data.opacity));
  }

  if (data.apply_to_both !== undefined) {
    formData.append("apply_to_both", String(data.apply_to_both));
  }

  if (data.color) {
    formData.append("color", data.color);
  }

  const response = await AIServiceAPI.post<ITaskSubmission>(
    "/api/sash-overlay",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

/**
 * Submit a multi-frame sash overlay task
 * Endpoint: POST /api/sash-overlay-multi-frame
 */
export const sashOverlayMultiFrameApi = async (
  data: ISashOverlayMultiFrameRequest
): Promise<ITaskSubmission> => {
  const formData = new FormData();

  formData.append("window_image", data.window_image);

  if (data.top_grids?.length) {
    data.top_grids.forEach((f) => formData.append("top_grids", f));
  }

  if (data.bottom_grids?.length) {
    data.bottom_grids.forEach((f) => formData.append("bottom_grids", f));
  }

  if (data.opacity !== undefined) {
    formData.append("opacity", String(data.opacity));
  }

  if (data.apply_to_both !== undefined) {
    formData.append("apply_to_both", String(data.apply_to_both));
  }

  if (data.color) {
    formData.append("color", data.color);
  }

  const response = await AIServiceAPI.post<ITaskSubmission>(
    "/api/sash-overlay-multi-frame",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

/**
 * Submit a deep bottom rail task — auto frame detection, no manual
 * coordinates/JSON rails data required.
 * Endpoint: POST /api/deep-bottom-rail-auto
 */
export const deepBottomRailApi = async (
  data: IDeepBottomRailRequest
): Promise<ITaskSubmission> => {
  const formData = new FormData();

  formData.append("window_image", data.window_image);

  if (data.apply_to_frames !== undefined) {
    formData.append("apply_to_frames", data.apply_to_frames);
  }

  if (data.rise_factor !== undefined) {
    formData.append("rise_factor", String(data.rise_factor));
  }

  const response = await AIServiceAPI.post<ITaskSubmission>(
    "/api/deep-bottom-rail-auto",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

/**
 * Get task status
 * Endpoint: GET /api/task/{task_id}
 */
export const getTaskStatusApi = async (
  taskId: string
): Promise<ITaskStatus> => {
  const response = await AIServiceAPI.get<ITaskStatus>(`/api/task/${taskId}`);
  return response.data;
};

/**
 * Download processed image
 * Endpoint: GET /api/download/{filename}
 */
export const downloadImageApi = async (filename: string): Promise<Blob> => {
  const response = await AIServiceAPI.get(`/api/download/${filename}`, {
    responseType: "blob",
  });
  return response.data;
};

/**
 * Fetch any AI service result URL as a stable in-memory blob URL.
 *
 * Uses the shared AIServiceAPI Axios instance (same base URL, interceptors)
 * so the download always succeeds regardless of CORS or URL expiry.
 * The caller is responsible for revoking the returned blob URL when done.
 *
 * @param resultUrl - Either a full URL (`https://...`) or a path (`/api/download/...`)
 */
export const fetchResultAsBlobUrl = async (resultUrl: string): Promise<string> => {
  // Already an in-memory blob URL — use directly
  if (resultUrl.startsWith('blob:')) return resultUrl;

  // Derive a relative path for AIServiceAPI
  let path: string;
  try {
    const base = new URL(resultUrl);
    path = base.pathname + base.search;
  } catch {
    // resultUrl is already a relative path
    path = resultUrl;
  }

  const response = await AIServiceAPI.get(path, { responseType: 'blob' });
  return URL.createObjectURL(response.data);
};

/**
 * Colorize a window template frame with a hex color (no AI, fast)
 * Endpoint: POST /api/colorize-window
 */
export const colorizeWindowApi = async (data: {
  window_image: File;
  color: string;
}): Promise<ITaskSubmission> => {
  const formData = new FormData();
  formData.append('window_image', data.window_image);
  formData.append('color', data.color);
  const response = await AIServiceAPI.post<ITaskSubmission>(
    '/api/colorize-window',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

/**
 * Debug files endpoint (development only)
 * Endpoint: GET /debug/files
 */
export const debugFilesApi = async (): Promise<any> => {
  const response = await AIServiceAPI.get("/debug/files");
  return response.data;
};
